import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { authService, type LockoutInfo } from '@/services/auth'
import { useAuthStore } from '@/stores/auth'
import { retryLanding, type RetryNotice } from '@/components/auth/lockedOutModel'
import { postLoginPath } from '@/lib/postLoginPath'

/**
 * What LockedOutScreen needs from its host, shared by the `/locked-out` route and the App.vue
 * overlay so the two can't drift: who to ask (`GET /auth/lockout-info`), Sign out and Try again.
 *
 * - `loadInfo()` fetches the owner / org / free seats; until it answers `owner` is undefined (the
 *   screen shows a placeholder card), and a failure leaves it null ("Ask your organization owner").
 * - `signOut()` logs out and goes to /login, busy (`signingOut`) until the logout settles.
 * - `retry()` runs `authStore.retryLockout()`. Still locked → `retryNotice` 'still-locked'; the
 *   check failed (network) → 'failed'. Unlocked → your dashboard from a signed-out page,
 *   /locked-out or an aborted first load (see `retryLanding`), otherwise the current view
 *   remounts (the same bump an org switch uses) so its data loads now that the API answers again.
 */
export function useLockedOut() {
  const authStore = useAuthStore()
  const router = useRouter()

  /** undefined while loading (or before `loadInfo`), null when the request failed. */
  const info = ref<LockoutInfo | null | undefined>(undefined)
  const signingOut = ref(false)
  const retrying = ref(false)
  const retryNotice = ref<RetryNotice | null>(null)

  // Each load is numbered so an answer that arrives after `reset()` (or a newer load) is dropped.
  let load = 0

  async function loadInfo() {
    const mine = ++load
    info.value = undefined
    try {
      const result = await authService.lockoutInfo()
      if (mine === load) info.value = result
    } catch {
      if (mine === load) info.value = null
    }
  }

  function reset() {
    load++
    info.value = undefined
    retryNotice.value = null
  }

  const owner = computed(() => (info.value === undefined ? undefined : (info.value?.owner ?? null)))
  // The membership list (GET /auth/organizations is exempt from the lockout) names the org even
  // when lockout-info can't.
  const orgName = computed(() => info.value?.orgName || authStore.activeOrg?.name || null)
  const freeSeats = computed(() => info.value?.freeSeats ?? null)

  async function signOut() {
    if (signingOut.value) return
    signingOut.value = true
    try {
      await authStore.logout()
      router.push('/login')
    } finally {
      signingOut.value = false
    }
  }

  async function retry() {
    if (retrying.value) return
    retrying.value = true
    retryNotice.value = null
    try {
      let unlocked: boolean
      try {
        unlocked = await authStore.retryLockout()
      } catch {
        retryNotice.value = 'failed'
        return
      }
      if (!unlocked) {
        retryNotice.value = 'still-locked'
        return
      }
      if (retryLanding(router.currentRoute.value) === 'dashboard') {
        await router.replace(postLoginPath(authStore.isRootUser ? 'root' : authStore.user?.role))
      } else {
        authStore.bumpOrgView()
      }
    } finally {
      retrying.value = false
    }
  }

  return { info, owner, orgName, freeSeats, signingOut, retrying, retryNotice, loadInfo, reset, signOut, retry }
}
